"use client";

import { memo } from "react";
import Link from "next/link";
import { Eye, Heart } from "lucide-react";
import type { EssenceSearchHit } from "@/lib/search/documents";
import { tintStyle } from "@/lib/essences/tint";
import { Badge } from "@/components/ui/badge";
import { EssenceTitle } from "./essence-title";
import { FavoriteButton } from "./favorite-button";
import { cn } from "@/lib/utils";

export type BrowseScope = "browse" | "favorites" | "popular";

interface EssenceCardProps {
  hit: EssenceSearchHit;
  onSelect?: () => void;
  scope?: BrowseScope;
  /** Which popularity metric to surface when scope is "popular". */
  metric?: "views" | "favorites";
  hasQuery?: boolean;
}

const SNIPPET_FIELD_LABELS: Record<string, string> = {
  ai_summary: "summary",
  description: "description",
  effects_text: "effects",
  sections_text: "sections",
  title: "title",
  author: "author",
};

/**
 * Typesense snippets wrap matches in <mark> but do not escape the source
 * text, so render them as React nodes instead of raw HTML.
 */
function renderSnippet(snippet: string): React.ReactNode[] {
  const parts = snippet.split(/(<mark>|<\/mark>)/);
  const nodes: React.ReactNode[] = [];
  let inMark = false;
  parts.forEach((part, i) => {
    if (part === "<mark>") {
      inMark = true;
      return;
    }
    if (part === "</mark>") {
      inMark = false;
      return;
    }
    if (!part) return;
    nodes.push(inMark ? <mark key={i}>{part}</mark> : <span key={i}>{part}</span>);
  });
  return nodes;
}

function dedupeStrings(values: string[]): string[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const key = value.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function EssenceCardInner({
  hit,
  onSelect,
  scope = "browse",
  metric = "views",
  hasQuery,
}: EssenceCardProps) {
  const doc = hit.document;
  const categories = dedupeStrings(doc.categories ?? []);

  const snippets = (hasQuery ? (hit.highlights ?? []) : [])
    .map((h) => {
      const field = String((h as { field?: string }).field ?? "");
      const snippet = String((h as { snippet?: string }).snippet ?? "");
      return { field, snippet };
    })
    .filter((h) => h.snippet && h.field !== "title" && h.field !== "author")
    .slice(0, 2);

  const count = metric === "favorites" ? (doc.favorite_count ?? 0) : (doc.view_count ?? 0);

  return (
    <article
      style={tintStyle(doc.author)}
      className={cn(
        "group/card relative flex h-full w-full flex-col rounded-xl border border-line bg-[rgb(16_14_27/0.72)] corner-glow hairline-top",
        "transition-[border-color,transform,box-shadow,background-color] duration-200 ease-out",
        "hover:-translate-y-0.5 hover:border-[rgb(167_139_250/0.4)] hover:bg-[rgb(20_17_34/0.85)] hover:shadow-[var(--shadow-glow-soft),0_24px_50px_-30px_rgb(0_0_0/0.9)]",
        "focus-within:border-[rgb(167_139_250/0.5)]"
      )}
    >
      <div className="flex items-start justify-between gap-2 px-5 pt-4">
        <div className="flex min-w-0 items-center gap-2 pt-1.5">
          <span
            aria-hidden
            className="size-1.5 shrink-0 rounded-full"
            style={{
              background: "hsl(var(--tint-h) 80% 72%)",
              boxShadow: "0 0 8px hsl(var(--tint-h) 80% 72% / 0.8)",
            }}
          />
          <span
            className="truncate text-[12.5px] font-medium tracking-[0.01em] text-ink-2"
            title={doc.author}
          >
            {doc.author}
          </span>
          {scope === "popular" && (
            <span className="ml-1 inline-flex shrink-0 items-center gap-1 font-mono text-[11.5px] tabular text-ink-3">
              {metric === "favorites" ? <Heart className="size-3" /> : <Eye className="size-3" />}
              {count.toLocaleString()}
            </span>
          )}
        </div>
        <FavoriteButton essenceId={doc.essence_id} className="relative z-10 -mr-2 -mt-1" />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 pt-2 pb-5">
        <Link
          href={`/essences/${encodeURIComponent(doc.essence_id)}`}
          onClick={(e) => {
            // Plain clicks open the modal; modified clicks keep native link behaviour.
            if (!onSelect || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
              return;
            e.preventDefault();
            onSelect();
          }}
          className="block w-full shrink-0 text-left outline-none after:absolute after:inset-0 after:rounded-xl after:content-[''] focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-accent"
          aria-label={`Open ${doc.title}`}
        >
          <EssenceTitle title={doc.title} as="span" size="card" className="block" />
        </Link>

        {/* Cards have a fixed height so the virtualized grid stays perfectly
            stable; long content scrolls inside the card instead. The
            relative z-10 lifts this above the stretched title link's ::after
            overlay so touch/wheel scrolling works inside the card; tapping
            elsewhere on the card still opens it. */}
        <div className="relative z-10 min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain pr-1">
          {doc.ai_summary ? (
            <p className="text-pretty text-[14px] leading-[1.6] text-ink-2">{doc.ai_summary}</p>
          ) : doc.description ? (
            <p className="text-pretty text-[14px] leading-[1.6] text-ink-2">{doc.description}</p>
          ) : null}

          {snippets.length > 0 && (
            <ul className="space-y-1.5 border-l border-line-accent/60 pl-3">
              {snippets.map((s, i) => (
                <li key={i} className="text-[12.5px] leading-[1.5] text-ink-3">
                  <span className="mr-1.5 micro-label text-[10px] text-ink-4">
                    in {SNIPPET_FIELD_LABELS[s.field] ?? s.field}
                  </span>
                  <span className="line-clamp-2 [&_mark]:font-medium">
                    {renderSnippet(s.snippet)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {categories.length > 0 && (
          <div className="flex shrink-0 flex-wrap gap-1.5 pt-1">
            {categories.slice(0, 4).map((cat) => (
              <Badge key={cat} variant="category">
                {cat}
              </Badge>
            ))}
            {categories.length > 4 && <Badge variant="neutral">+{categories.length - 4}</Badge>}
          </div>
        )}
      </div>
    </article>
  );
}

export const EssenceCard = memo(EssenceCardInner);
